import { CopyIcon, ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { memo, useState } from 'react'
import { useCopyToClipboard } from 'usehooks-ts'

import { Action, Actions } from '@/components/elements/actions'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/lib/toast'
import type { ChatMessage } from '@/lib/types'
import { reportFlag } from '@/service/ai.api'

type FeedbackSource = 'thumbs_up' | 'thumbs_down'
type FeedbackType = 'bug_report' | 'positive_example' | 'question'

const FEEDBACK_TYPE_OPTIONS: FeedbackType[] = ['bug_report', 'positive_example', 'question']

function formatTurnSk(turnIndex: number): string {
  return `TURN#${String(turnIndex).padStart(3, '0')}`
}

function defaultFeedbackType(source: FeedbackSource): FeedbackType {
  return source === 'thumbs_up' ? 'positive_example' : 'bug_report'
}

function getTriageReference(response: Awaited<ReturnType<typeof reportFlag>>): string {
  if (!response) return ''
  return response.triage_item_url ?? response.url ?? response.triage_item_id ?? response.id ?? ''
}

export function PureMessageActions({
  chatId,
  message,
  isLoading,
  turnIndex,
  feedbackOnly = false,
}: {
  chatId: string
  message: ChatMessage
  isLoading: boolean
  /** 1-based AI turn number used to derive TURN#001, TURN#002, etc. Only required for assistant messages. */
  turnIndex?: number
  feedbackOnly?: boolean
}) {
  const t = useTranslations('orderForm.messageActions')
  const [_, copyToClipboard] = useCopyToClipboard()
  const [isFlagging, setIsFlagging] = useState(false)
  const [flaggedSource, setFlaggedSource] = useState<FeedbackSource | null>(null)
  const [feedbackSource, setFeedbackSource] = useState<FeedbackSource | null>(null)
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug_report')
  const [feedbackNotes, setFeedbackNotes] = useState('')

  if (isLoading) {
    return null
  }

  const textFromParts = message.parts
    ?.filter((part) => part.type === 'text')
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('\n')
    .trim()

  const handleCopy = async () => {
    if (!textFromParts) {
      toast.error(t('noTextToCopy'))
      return
    }

    await copyToClipboard(textFromParts)
    toast.success(t('copiedToClipboard'))
  }

  const openFeedback = (source: FeedbackSource) => {
    if (isFlagging || flaggedSource || !chatId || turnIndex == null) return
    setFeedbackType(defaultFeedbackType(source))
    setFeedbackNotes('')
    setFeedbackSource(source)
  }

  const handleFlag = async () => {
    if (isFlagging || flaggedSource || !feedbackSource || !chatId || turnIndex == null) return

    setIsFlagging(true)
    try {
      const response = await reportFlag({
        session_id: chatId,
        turn_sk: formatTurnSk(turnIndex),
        source: feedbackSource,
        type: feedbackType,
        notes: feedbackNotes.trim(),
      })
      setFlaggedSource(feedbackSource)
      setFeedbackSource(null)

      const triageReference = getTriageReference(response)
      if (triageReference) {
        await copyToClipboard(triageReference)
        toast.success(t('feedbackSubmittedWithReference'))
      } else {
        toast.success(t('feedbackSubmitted'))
      }
    } catch {
      toast.error(t('feedbackSubmitFailed'))
    } finally {
      setIsFlagging(false)
    }
  }

  const canCopy = Boolean(textFromParts)
  if (message.role === 'system') return null

  // User messages get edit (on hover) and copy actions
  if (message.role === 'user') {
    if (!canCopy) return null

    return (
      // Revealed on hover/focus with a pointer (the bubble is the content; its copy button is
      // secondary), always shown on touch where there is no hover.
      <Actions className="-mr-0.5 justify-end opacity-0 transition-opacity duration-150 group-hover/message:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
        <div className="relative">
          <Action onClick={handleCopy} tooltip={t('copy')}>
            <CopyIcon className="size-3 text-white/60" />
          </Action>
        </div>
      </Actions>
    )
  }

  return (
    <Actions className="justify-start">
      {canCopy && !feedbackOnly && (
        <Action onClick={handleCopy} tooltip={t('copy')}>
          <CopyIcon className="size-3 text-white/60" />
        </Action>
      )}
      {turnIndex != null && (
        <>
          <Popover
            open={feedbackSource === 'thumbs_up'}
            onOpenChange={(open) => (open ? openFeedback('thumbs_up') : setFeedbackSource(null))}
          >
            <PopoverTrigger asChild>
              <Action
                disabled={isFlagging || flaggedSource !== null}
                label={flaggedSource === 'thumbs_up' ? t('flagged') : t('goodResponse')}
              >
                <ThumbsUpIcon
                  fill={flaggedSource === 'thumbs_up' ? 'currentColor' : 'none'}
                  className={
                    flaggedSource === 'thumbs_up'
                      ? 'text-foreground size-3'
                      : 'text-foreground/60 size-3'
                  }
                />
              </Action>
            </PopoverTrigger>
            <FeedbackPopoverContent
              feedbackType={feedbackType}
              isSubmitting={isFlagging}
              notes={feedbackNotes}
              onNotesChange={setFeedbackNotes}
              onSubmit={handleFlag}
              onTypeChange={setFeedbackType}
            />
          </Popover>
          <Popover
            open={feedbackSource === 'thumbs_down'}
            onOpenChange={(open) => (open ? openFeedback('thumbs_down') : setFeedbackSource(null))}
          >
            <PopoverTrigger asChild>
              <Action
                disabled={isFlagging || flaggedSource !== null}
                label={flaggedSource === 'thumbs_down' ? t('flagged') : t('reportIssue')}
              >
                <ThumbsDownIcon
                  fill={flaggedSource === 'thumbs_down' ? 'currentColor' : 'none'}
                  className={
                    flaggedSource === 'thumbs_down'
                      ? 'text-foreground size-3'
                      : 'text-foreground/60 size-3'
                  }
                />
              </Action>
            </PopoverTrigger>
            <FeedbackPopoverContent
              feedbackType={feedbackType}
              isSubmitting={isFlagging}
              notes={feedbackNotes}
              onNotesChange={setFeedbackNotes}
              onSubmit={handleFlag}
              onTypeChange={setFeedbackType}
            />
          </Popover>
        </>
      )}
    </Actions>
  )
}

export const MessageActions = memo(PureMessageActions, (prevProps, nextProps) => {
  if (prevProps.isLoading !== nextProps.isLoading) {
    return false
  }

  return true
})

function FeedbackPopoverContent({
  feedbackType,
  isSubmitting,
  notes,
  onNotesChange,
  onSubmit,
  onTypeChange,
}: {
  feedbackType: FeedbackType
  isSubmitting: boolean
  notes: string
  onNotesChange: (notes: string) => void
  onSubmit: () => void
  onTypeChange: (type: FeedbackType) => void
}) {
  const t = useTranslations('orderForm.messageActions')
  return (
    <PopoverContent align="start" className="w-75 p-3">
      <div className="flex flex-col gap-3">
        <div>
          <p className="text-xs font-medium text-white/90">{t('sendFeedback')}</p>
          <p className="mt-1 text-[11px] leading-4 text-white/45">{t('addNoteDescription')}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] text-white/45">{t('type')}</label>
          <Select
            value={feedbackType}
            onValueChange={(value) => onTypeChange(value as FeedbackType)}
          >
            <SelectTrigger size="sm" className="px-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70] min-w-35">
              {FEEDBACK_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option} value={option} showCheck>
                  {t(`feedbackType.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] text-white/45">{t('note')}</label>
          <Textarea
            className="min-h-24 resize-none border-white/10 bg-white/5 text-xs text-white placeholder:text-white/30"
            maxLength={1000}
            onChange={(event) => onNotesChange(event.target.value)}
            placeholder={t('notePlaceholder')}
            value={notes}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            className="h-8 border-white/10 px-3 text-xs"
            disabled={isSubmitting}
            onClick={onSubmit}
            size="sm"
            type="button"
            variant="outline"
          >
            {isSubmitting ? t('sending') : t('submit')}
          </Button>
        </div>
      </div>
    </PopoverContent>
  )
}
